import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { HeartHandshake, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Loader2, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS = {
  ok:                  { tone: "border-emerald-200 bg-emerald-50 text-emerald-900", Icon: CheckCircle2, label: "Healthy" },
  signature_mismatch:  { tone: "border-rose-200 bg-rose-50 text-rose-900",         Icon: XCircle,       label: "Signature mismatch" },
  endpoint_missing:    { tone: "border-amber-200 bg-amber-50 text-amber-900",      Icon: AlertTriangle, label: "Endpoint missing" },
  unreachable:         { tone: "border-rose-200 bg-rose-50 text-rose-900",         Icon: XCircle,       label: "Unreachable" },
  timeout:             { tone: "border-amber-200 bg-amber-50 text-amber-900",      Icon: Clock,         label: "Timed out" },
  unexpected_response: { tone: "border-amber-200 bg-amber-50 text-amber-900",      Icon: AlertTriangle, label: "Unexpected response" },
  error:               { tone: "border-rose-200 bg-rose-50 text-rose-900",         Icon: XCircle,       label: "Error" },
  unknown:             { tone: "border-border bg-muted/30 text-primary",            Icon: AlertTriangle, label: "Unknown" },
};

export default function CareNetHealthCard() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const ping = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/carenet/health");
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { ping(); }, [ping]);

  const meta = result ? (STATUS[result.status] || STATUS.unknown) : STATUS.unknown;
  const Icon = meta.Icon;

  return (
    <div className={`rounded-lg border p-5 ${meta.tone}`} data-testid="carenet-health-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <HeartHandshake size={18} className="shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-widest opacity-80">Care Net SSO</div>
            <div className="mt-0.5 font-serif text-lg flex items-center gap-2">
              <Icon size={14} />
              <span>{meta.label}</span>
              {result?.latency_ms != null && <span className="text-xs opacity-70">· {result.latency_ms} ms</span>}
            </div>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={ping} disabled={loading} data-testid="carenet-health-refresh">
          {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
        </Button>
      </div>
      {result && (
        <div className="mt-3 space-y-1 text-xs" data-testid="carenet-health-detail">
          {result.detail && <div className="opacity-90">{result.detail}</div>}
          <div className="opacity-70">
            POST <code className="font-mono">{result.verify_url}</code>
            {result.http_status != null && ` → HTTP ${result.http_status}`}
          </div>
          {result.checked_at && (
            <div className="opacity-60 text-[10px]">Last checked {new Date(result.checked_at).toLocaleString()}</div>
          )}
        </div>
      )}
    </div>
  );
}
